/* eslint-disable brace-style */
import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
// "firebase": "^9.15.0",
// Must match the Firebase project the backend's Admin SDK authenticates as
// (see purplept-dev-firebase-adminsdk-serviceAccountKey.json's project_id —
// despite the filename, it's actually "purple-prn"). A mismatch here causes
// every getToken() result to fail server-side sends with
// "messaging/mismatched-credential" / "SenderId mismatch".
const firebaseConfig = {
  apiKey: "AIzaSyDERlDU5PqQBwWX7fQCkAqtRyjUEBXUUpo",
  authDomain: "purple-prn.firebaseapp.com",
  projectId: "purple-prn",
  storageBucket: "purple-prn.firebasestorage.app",
  messagingSenderId: "219002367802",
  appId: "1:219002367802:web:1c92639359fc618e1d6a03",
  measurementId: "G-ED6V2RY39E",
};

// Initialize Firebase
initializeApp(firebaseConfig);

const messaging = getMessaging();
const publicKey = process?.env?.REACT_APP_VAPID_KEY;

/**
 * Resolves once the registration has an activated worker. getToken() on a
 * registration whose worker is still installing fails with "Subscription
 * failed - no active Service Worker" (e.g. first login after site data is
 * cleared).
 */
const waitForActiveWorker = (registration) => {
  if (registration.active) {
    return Promise.resolve(registration);
  }
  const worker = registration.installing || registration.waiting;
  if (!worker) {
    return Promise.resolve(registration);
  }
  return new Promise((resolve) => {
    const onStateChange = () => {
      if (worker.state === "activated" || worker.state === "redundant") {
        worker.removeEventListener("statechange", onStateChange);
        resolve(registration);
      }
    };
    worker.addEventListener("statechange", onStateChange);
  });
};

// Register the messaging service worker once per page load and reuse it.
// Re-registering on every token request is what let concurrent getToken()
// calls rotate the push subscription, leaving the backend with a token the
// browser no longer listens on.
let registrationPromise = null;
const getMessagingRegistration = () => {
  if (!registrationPromise) {
    const swPath = `${process.env.REACT_APP_BASENAME}/firebase-messaging-sw.js`;
    registrationPromise = navigator.serviceWorker
      .register(swPath)
      .then(waitForActiveWorker)
      .catch((err) => {
        registrationPromise = null;
        throw err;
      });
  }
  return registrationPromise;
};

// Firebase only routes a push to the service worker's onBackgroundMessage
// when the tab is NOT focused. A focused/foreground tab gets it here via
// onMessage instead, and the browser does not auto-display anything for
// that case — we have to show it ourselves, same as the service worker
// does for background pushes (see firebase-messaging-sw.js).
onMessage(messaging, async (payload) => {
  try {
    // Chrome refuses `new Notification()` on a page with an active service
    // worker ("Illegal constructor: use ServiceWorkerRegistration.showNotification()
    // instead"), so this has to go through the SW registration too.
    // Not navigator.serviceWorker.ready: the messaging worker's scope does not
    // cover every app route, so `ready` can stay pending forever.
    const registration = await getMessagingRegistration();
    await registration.showNotification(payload.data?.title || "", {
      body: payload.data?.body,
      icon: payload.data?.icon || "/icon.png",
      data: {
        url: payload.data?.click_action,
        user_id: payload.data?.userId || null,
        notification_type: payload.data?.type || "",
        target_screen: payload.data?.target_screen || "",
      },
    });
  } catch (err) {
    console.error("Failed to show foreground notification:", err);
  }
});

export const getBrowsername = () => {
  let nAgt = navigator.userAgent;
  let browserName = "";
  let nameOffset, verOffset;

  // In Opera, the true version is after "OPR" or after "Version"
  if ((verOffset = nAgt.indexOf("OPR")) !== -1) {
    browserName = "Opera";
  }
  // In MS Edge, the true version is after "Edg" in userAgent
  else if ((verOffset = nAgt.indexOf("Edg")) !== -1) {
    browserName = "Edge";
  }
  // In MSIE, the true version is after "MSIE" in userAgent
  else if ((verOffset = nAgt.indexOf("MSIE")) !== -1) {
    browserName = "Microsoft Internet Explorer";
  }
  // In Chrome, the true version is after "Chrome"
  else if ((verOffset = nAgt.indexOf("Chrome")) !== -1) {
    browserName = "Chrome";
  }
  // In Safari, the true version is after "Safari" or after "Version"
  else if ((verOffset = nAgt.indexOf("Safari")) !== -1) {
    browserName = "Safari";
  }
  // In Firefox, the true version is after "Firefox"
  else if ((verOffset = nAgt.indexOf("Firefox")) !== -1) {
    browserName = "Firefox";
  }
  // In most other browsers, "name/version" is at the end of userAgent
  else if (
    (nameOffset = nAgt.lastIndexOf(" ") + 1) <
    (verOffset = nAgt.lastIndexOf("/"))
  ) {
    browserName = nAgt.substring(nameOffset, verOffset);
    if (browserName.toLowerCase() === browserName.toUpperCase()) {
      browserName = navigator.appName;
    }
  }

  return browserName;
};

/**
 * This function registers a service worker and returns a token for Firebase messaging.
 * @returns The `requestForToken` function is returning a promise that resolves to a Firebase Cloud
 * Messaging (FCM) token. The function first registers the service worker located at
 * `/firebase-messaging-sw.js`, and then calls the `getToken` function with the
 * `messaging` object, a `vapidKey` and the `serviceWorkerRegistration` object. If the `getToken`
 */
const FIREBASE_MESSAGING_DB_NAMES = [
  "firebase-messaging-database",
  "firebase-installations-database",
];

function isIndexedDbVersionError(err) {
  return err?.name === "VersionError" || /version/i.test(err?.message || "");
}
function resetFirebaseMessagingIndexedDb() {
  return Promise.all(
    FIREBASE_MESSAGING_DB_NAMES.map(
      (name) =>
        new Promise((resolve) => {
          const req = indexedDB.deleteDatabase(name);
          req.onsuccess = () => resolve();
          req.onerror = () => resolve();
          req.onblocked = () => resolve();
        }),
    ),
  );
}

const INDEXEDDB_RESET_GUARD_KEY = "firebaseMessagingIndexedDbResetAttempted";

const fetchToken = async () => {
  let browserName = getBrowsername();
  if (browserName.toLowerCase() === "edge") {
    return null;
  }
  let registration = await getMessagingRegistration();

  const attemptGetToken = () =>
    getToken(messaging, {
      vapidKey: publicKey,
      serviceWorkerRegistration: registration,
    });

  let token = await attemptGetToken().catch(async (err) => {
    if (!isIndexedDbVersionError(err)) {
      console.error("getToken failed:", err);
      return "";
    }

    if (sessionStorage.getItem(INDEXEDDB_RESET_GUARD_KEY)) {
      console.error(
        "IndexedDB VersionError persisted after a reload-based reset already this session — not retrying again to avoid a reload loop.",
        err,
      );
      return "";
    }

    await resetFirebaseMessagingIndexedDb();
    sessionStorage.setItem(INDEXEDDB_RESET_GUARD_KEY, "1");
    window.location.reload();
    return new Promise(() => {});
  });

  if (token) {
    sessionStorage.removeItem(INDEXEDDB_RESET_GUARD_KEY);
  }

  return token;
};

// Callers that overlap (login, layouts, 2FA screen) share one in-flight
// getToken() instead of racing each other.
let tokenPromise = null;
export const requestForToken = () => {
  if (!tokenPromise) {
    tokenPromise = fetchToken().finally(() => {
      tokenPromise = null;
    });
  }
  return tokenPromise;
};
